import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  pmGet,
  pmPost,
  type PmProject,
  type PmTrackerOpportunity,
  type PmTrackerPhaseRow,
} from '../api';
import './ResourceTracker.css';
import { SelectField, PROMPT } from '@platform/ui-kit';
import { HelpPanel, type HelpContent } from '../../portfolio_definition/HelpPanel';
import { withFinalizedRule } from '../finalizedRuleHelp';

/**
 * Resource Tracker — actual effort against the plan, per opportunity and phase.
 *
 * Cloned from `/Users/glucic/src/ai_readiness_template/views/roadmap-execution/resource-tracker.ejs`.
 *
 * THE MIRROR OF THE BUDGET TRACKER, in person-days rather than money, and with the same
 * precedence: a typed manual figure wins, and clearing it hands the phase back to the
 * effort log, whose rollup shows through as the input's placeholder.
 *
 * IT SHOWS ITS WORKING, which the Budget Tracker does not. Planned effort is not stored —
 * it is `planned FTE × the phase's WORKING days`, and both factors get their own column, so
 * a planned figure can be argued with rather than just accepted. The phase window
 * (start → end) sits beside each phase label for the same reason.
 *
 * An entry's effort is `fte × duration_days`; the log sums them per opportunity and phase.
 */

const PHASE_COLORS: Record<string, string> = {
  Assessment_Planning: '#90caf9',
  Design_Architecture: '#ce93d8',
  Development: '#a5d6a7',
  Testing_Validation: '#ffcc80',
  GoLive_Optimization: '#ef9a9a',
  Agile_Delivery: '#80cbc4',
};

/** One decimal, then the unit. The source's `pd`. */
const pdFmt = (n: unknown) =>
  `${(Math.round((Number(n) || 0) * 10) / 10).toLocaleString('en-US')} pd`;
/** The same rounding without a unit — used for FTE columns. The source's `fte`. */
const fteFmt = (n: unknown) => (Math.round((Number(n) || 0) * 10) / 10).toLocaleString('en-US');

/**
 * The variance dead zone is ±0.05 PERSON-DAYS here, not the Budget Tracker's ±0.5 dollars —
 * the units are three orders of magnitude apart and the thresholds are set accordingly.
 */
const varClass = (v: number) =>
  v < -0.05 ? 'pm-rt-varpos' : v > 0.05 ? 'pm-rt-varneg' : 'pm-rt-varzero';
/** A MINUS SIGN (−, U+2212), as the source uses. */
const varStr = (v: number) => `${v > 0.05 ? '+' : v < -0.05 ? '−' : ''}${pdFmt(Math.abs(v))}`;

interface EffortEntry {
  id: number;
  opportunity_id: string | null;
  phase: string;
  resource: string | null;
  vendor: string | null;
  start_date: string | null;
  duration_days: number;
  fte: number;
  reference?: string | null;
  note?: string | null;
  effort?: number;
}

interface ResourceTrackerData {
  success: boolean;
  error?: string;
  project: PmProject;
  phases: { key: string; label: string }[];
  shared: { phases: PmTrackerPhaseRow[] };
  opportunities: PmTrackerOpportunity[];
  entries: EffortEntry[];
}

/**
 * "How this works" — cloned from `ai_readiness_template/views/roadmap-execution/resource-tracker.ejs`,
 * its `rtBreakdownHelp` / `rtLogHelp` objects. Copy, colours and section order are the source's;
 * only the link hrefs are rewritten to this platform's routes, and the entities in
 * fields HelpPanel renders as TEXT (title, section label, term, link label) are
 * decoded, because only the HTML fields go through `dangerouslySetInnerHTML`.
 */
const RT_HELPS: HelpContent[] = [
  {
    tabLabel: 'Effort breakdown',
    title: 'Resource Tracker — Breakdown',
    icon: '📊',
    purpose: "Track <strong>actual effort against the plan</strong> for every opportunity, phase by phase, in <strong>person&#8209;days</strong>. A project-wide <strong>Shared / Project</strong> bucket sits alongside each <strong>opportunity</strong>.",
    doHere: [
      "An opportunity whose methodology is <strong>None</strong> has <strong>no phases</strong> &mdash; it appears here as a single <strong>Delivery</strong> row covering its whole window, and is fully trackable just like any other initiative.",
      "Pick a <strong>Project</strong> — planned effort loads per phase (Planned FTE × the phase's working days).",
      "Type an <strong>Actual</strong> effort straight into any phase row — every phase is editable.",
      "Or log entries on the <strong>Effort log</strong> tab; they roll up into the phase's actual effort automatically.",
      "Read the <strong>Variance</strong> column, then click <strong>Save</strong> to persist the actuals you typed."
    ],
    fits: 'Resource Plan &rarr; <strong>Resource Tracker</strong> &rarr; Execution Control Center',
    firstTime: "Pick a project, expand an opportunity, type an actual effort (or log effort), then Save.",
    sections: [
      { label: 'How effort is measured', type: 'defs', items: [
        { term: 'Planned FTE', color: '#607d8b', def: "The effective FTE from the <strong>Resource Plan</strong> for that opportunity-phase." },
        { term: 'Working days', color: '#8e24aa', def: "Business days in the phase window (from the roadmap dates). Planned effort = Planned FTE × working days." },
        { term: 'Actual', color: '#00897b', def: "Actual person-days — type it directly, or let the effort log fill it in." },
        { term: 'Variance', color: '#455a64', def: "Actual − Planned effort. <span style=\"color:#2e7d32;font-weight:700;\">Green</span> under plan, <span style=\"color:#c62828;font-weight:700;\">red</span> over." }
      ] },
      { label: 'Typing actuals vs. the log', type: 'html', html: "Every phase has an editable Actual box (person-days). A number you type is a <strong>manual actual</strong> and always wins. Leave it blank and the phase falls back to the sum of its <strong>logged effort</strong> (shown as the faint placeholder)." },
      { label: "What's new", type: 'html', html: "This screen is now <strong>per-opportunity</strong> (Shared bucket + each opportunity by phase), with <strong>editable</strong> actual effort on every phase and a dedicated <strong>Effort log</strong> tab." }
    ],
    links: [ { label: '← Resource Plan', href: '/project_management/program-management/budget-resources/resource-plan' } ]
  },
  {
    tabLabel: 'Effort log',
    title: 'Effort Log',
    icon: '🧾',
    purpose: "Record who worked on each <strong>scope and phase</strong>, at what FTE and for how many days. Each entry contributes <strong>FTE × days</strong> person-days to that phase's Actual on the Breakdown tab.",
    doHere: [
      "Use the single <strong>add row</strong> at the top: choose Scope and Phase, then enter Resource, an optional <strong>Vendor</strong>, FTE, Days and a start date.",
      "Click <strong>+ Add</strong> — the entry saves and appears in the history; the phase's actual effort updates automatically.",
      "Delete an entry with the <strong>×</strong> to reduce the rollup."
    ],
    fits: 'Resource Tracker &rarr; <strong>Effort log</strong> (per opportunity &amp; phase)',
    firstTime: "Fill the top add-row (scope, phase, resource, FTE, days), click + Add, then check the Breakdown tab.",
    sections: [
      { label: 'What each column means', type: 'defs', items: [
        { term: 'Scope', color: '#6a1b9a', def: "Shared / Project, or a specific opportunity." },
        { term: 'Resource', color: '#00897b', def: "Person or role. <strong>Vendor</strong> (optional, free text) records who supplied them." },
        { term: 'FTE × Days', color: '#1976d2', def: "Effort in person-days for the entry." }
      ] },
      { label: 'How it rolls up', type: 'html', html: "All entries for an opportunity + phase are summed into that phase's <strong>Actual effort</strong> on the Breakdown tab. A <strong>manual actual</strong> typed there takes precedence; clear it to fall back to the logged total." },
      { label: "What's new", type: 'html', html: "The effort log is now its own tab with a single inline add-row, and each entry can carry an optional free-text <strong>Vendor</strong>." }
    ],
    links: [ { label: '← Resource Plan', href: '/project_management/program-management/budget-resources/resource-plan' } ]
  },
];

export function ResourceTracker() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [projects, setProjects] = useState<PmProject[]>([]);
  const [currentProject, setCurrentProject] = useState('');
  const [prog, setProg] = useState<ResourceTrackerData | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [activeTab, setActiveTab] = useState<'breakdown' | 'log'>('breakdown');
  const [dirty, setDirty] = useState(false);
  const [saveLabel, setSaveLabel] = useState('Save');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [effScope, setEffScope] = useState('');
  const [effPhase, setEffPhase] = useState('');
  const [effResource, setEffResource] = useState('');
  const [effVendor, setEffVendor] = useState('');
  const [effFte, setEffFte] = useState('');
  const [effDays, setEffDays] = useState('');
  const [effStart, setEffStart] = useState('');

  const onSelect = useCallback(async (id: string) => {
    setCurrentProject(id);
    setError(null);
    if (!id) {
      setProg(null);
      return;
    }
    try {
      const d = await pmGet<ResourceTrackerData>(
        `/resource-tracker/program-data?delivery_project_id=${encodeURIComponent(id)}`,
      );
      if (!d.success) {
        setError(d.error ?? 'failed');
        return;
      }
      setExpanded(new Set());
      setProg(d);
      setEffScope('');
      setEffPhase(d.phases[0]?.key ?? '');
      setDirty(false);
    } catch (e) {
      setError((e as Error).message);
    }
  }, []);

  useEffect(() => {
    pmGet<{ projects: PmProject[] }>('/br/projects')
      .then((d) => {
        const list = d.projects ?? [];
        setProjects(list);
        // The Execution Control Center's "Open Resource Tracker" carries the programme here.
        const qp = searchParams.get('delivery_project_id');
        if (qp && list.some((p) => p.delivery_project_id === qp)) void onSelect(qp);
      })
      .catch((e) => setError((e as Error).message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scopePhases = useCallback(
    (id: string): PmTrackerPhaseRow[] => {
      if (!prog) return [];
      if (id === 'shared') return prog.shared.phases;
      return prog.opportunities.find((x) => `opp:${x.opportunity_id}` === id)?.phases ?? [];
    },
    [prog],
  );
  const scopeOppId = (id: string) => (id === 'shared' ? '' : id.slice(4));

  const entriesFor = useCallback(
    (oppId: string, phase?: string) =>
      (prog?.entries ?? []).filter(
        (e) => (e.opportunity_id || '') === oppId && (!phase || e.phase === phase),
      ),
    [prog],
  );
  const loggedOf = useCallback(
    (oppId: string, phase?: string) =>
      entriesFor(oppId, phase).reduce(
        (s, e) => s + (Number(e.fte) || 0) * (Number(e.duration_days) || 0),
        0,
      ),
    [entriesFor],
  );
  const actualOf = useCallback(
    (r: PmTrackerPhaseRow, oppId: string) => {
      if (r.manual != null && (r.manual as unknown) !== '') return Number(r.manual);
      return loggedOf(oppId, r.phase);
    },
    [loggedOf],
  );

  const plannedTot = useCallback(
    (id: string) => scopePhases(id).reduce((s, r) => s + (Number(r.planned_effort) || 0), 0),
    [scopePhases],
  );
  const actualTot = useCallback(
    (id: string) => {
      const oppId = scopeOppId(id);
      return scopePhases(id).reduce((s, r) => s + actualOf(r, oppId), 0);
    },
    [scopePhases, actualOf],
  );

  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const expandAll = (on: boolean) => {
    if (!prog) return;
    setExpanded(
      on
        ? new Set(['shared', ...prog.opportunities.map((o) => `opp:${o.opportunity_id}`)])
        : new Set(),
    );
  };

  const setManual = (scopeId: string, phase: string, val: string) => {
    const row = scopePhases(scopeId).find((x) => x.phase === phase);
    if (!row) return;
    row.manual = val === '' ? null : (val as unknown as number);
    setProg((p) => (p ? { ...p } : p));
    setDirty(true);
  };

  const save = async () => {
    if (!currentProject || !prog) return;
    setSaving(true);
    setSaveLabel('Saving…');
    const actual = (r: PmTrackerPhaseRow) =>
      (r.manual as unknown) === '' || r.manual == null ? null : Number(r.manual);
    try {
      const d = await pmPost<{ success: boolean; error?: string }>(
        '/resource-tracker/program-save',
        {
          delivery_project_id: currentProject,
          shared: prog.shared.phases.map((r) => ({ phase: r.phase, actual: actual(r) })),
          opportunities: prog.opportunities.map((o) => ({
            opportunity_id: o.opportunity_id,
            actuals: o.phases.map((r) => ({ phase: r.phase, actual: actual(r) })),
          })),
        },
      );
      if (d.success) {
        setDirty(false);
        setSaveLabel('Saved ✓');
        setTimeout(() => setSaveLabel('Save'), 1100);
      } else {
        setError(d.error ?? 'failed');
        setSaveLabel('Save');
      }
    } catch (e) {
      setError((e as Error).message);
      setSaveLabel('Save');
    } finally {
      setSaving(false);
    }
  };

  const addEntry = async () => {
    if (!prog) return;
    if (effFte === '' || isNaN(Number(effFte))) {
      setError('Enter a valid FTE.');
      return;
    }
    if (effDays === '' || isNaN(Number(effDays))) {
      setError('Enter valid duration days.');
      return;
    }
    try {
      const d = await pmPost<{ success: boolean; error?: string; id: number }>(
        '/resource-tracker/effort/program-save',
        {
          delivery_project_id: currentProject,
          entry: {
            opportunity_id: effScope || null,
            phase: effPhase,
            resource: effResource,
            vendor: effVendor,
            fte: Number(effFte),
            duration_days: Number(effDays),
            start_date: effStart || null,
          },
        },
      );
      if (!d.success) {
        setError(d.error ?? 'failed');
        return;
      }
      setProg((p) =>
        p
          ? {
              ...p,
              entries: [
                ...p.entries,
                {
                  id: d.id,
                  opportunity_id: effScope,
                  phase: effPhase,
                  resource: effResource,
                  vendor: effVendor,
                  fte: Number(effFte),
                  duration_days: Number(effDays),
                  start_date: effStart,
                },
              ],
            }
          : p,
      );
      setEffResource('');
      setEffVendor('');
      setEffFte('');
      setEffDays('');
      setEffStart('');
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const delEntry = async (eid: number) => {
    if (!window.confirm('Delete this effort entry?')) return;
    try {
      const d = await pmPost<{ success: boolean; error?: string }>(
        '/resource-tracker/effort/program-delete',
        { delivery_project_id: currentProject, id: eid },
      );
      if (!d.success) {
        setError(d.error ?? 'failed');
        return;
      }
      setProg((p) => (p ? { ...p, entries: p.entries.filter((e) => e.id !== eid) } : p));
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const phaseLabel = (key: string) => prog?.phases.find((x) => x.key === key)?.label ?? key;
  const scopeName = (oppId: string) => (!oppId ? '🌐 Shared' : `📌 ${oppId}`);

  const totals = prog
    ? prog.opportunities.reduce(
        (acc, o) => {
          const k = `opp:${o.opportunity_id}`;
          return { planned: acc.planned + plannedTot(k), actual: acc.actual + actualTot(k) };
        },
        { planned: plannedTot('shared'), actual: actualTot('shared') },
      )
    : { planned: 0, actual: 0 };
  const totalVar = totals.actual - totals.planned;

  const renderScope = (id: string, title: string, op: PmTrackerOpportunity | null) => {
    const isShared = !op;
    const open = expanded.has(id);
    const accent = isShared ? '#6a1b9a' : '#00897b';
    const oppId = scopeOppId(id);
    const phases = scopePhases(id);
    const pl = plannedTot(id);
    const ac = actualTot(id);
    const tv = ac - pl;

    const rows = [
      <tr className="grp" key={id} onClick={() => toggle(id)}>
        <td className="ind">
          <i
            className="material-icons pm-rt-caret"
            style={{ transform: `rotate(${open ? 90 : 0}deg)` }}
          >
            chevron_right
          </i>
        </td>
        <td>
          <div className="pm-rt-scopecell">
            <span className="pm-rt-dot" style={{ background: accent }} />
            {title} {op ? <span className="pm-rt-oppname">{op.name || ''}</span> : null}
          </div>
        </td>
        <td className="num" />
        <td className="num" />
        <td className="num">{pdFmt(pl)}</td>
        <td className="num">{pdFmt(ac)}</td>
        <td className={`num ${varClass(tv)}`}>{varStr(tv)}</td>
      </tr>,
    ];

    phases.forEach((r) => {
      const logged = loggedOf(oppId, r.phase);
      const v = actualOf(r, oppId) - (Number(r.planned_effort) || 0);
      const typed = r.manual != null && (r.manual as unknown) !== '';
      rows.push(
        <tr className="phrow" key={`${id}-${r.phase}`} style={{ display: open ? '' : 'none' }}>
          <td className="ind" />
          <td className="ph-cell">
            <span
              className="pm-rt-phbox"
              style={{ background: PHASE_COLORS[r.phase] || '#b0bec5' }}
              title={r.label}
            />
            <span className="pm-rt-phlabel">{r.label}</span>
            {r.start && r.end ? (
              <span className="pm-rt-muted" style={{ fontSize: 10.5 }}>
                &nbsp;·&nbsp;{r.start} → {r.end}
              </span>
            ) : null}
          </td>
          <td className="num">{fteFmt(r.planned_fte)}</td>
          <td className="num">{Number(r.working_days) || 0} d</td>
          <td className="num">{pdFmt(r.planned_effort)}</td>
          <td className="num">
            <input
              type="number"
              className="pm-rt-ovinput"
              min={0}
              step={0.1}
              /* The effort-log rollup shows through as the placeholder — that is the fallback. */
              placeholder={logged > 0 ? String(Math.round(logged * 10) / 10) : '0'}
              value={typed ? String(r.manual) : ''}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => setManual(id, r.phase, e.target.value)}
            />
          </td>
          <td className={`num ${varClass(v)}`}>{varStr(v)}</td>
        </tr>,
      );
    });
    return rows;
  };

  const entries = prog
    ? [...prog.entries].sort(
        (a, b) =>
          String(a.start_date || '').localeCompare(String(b.start_date || '')) || a.id - b.id,
      )
    : [];
  const logSum = entries.reduce(
    (s, e) => s + (Number(e.fte) || 0) * (Number(e.duration_days) || 0),
    0,
  );

  return (
    <div
      className="page"
      style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}
    >
      <HelpPanel helps={RT_HELPS.map((h) => withFinalizedRule(h))} />
      <div className="pm-rt-head">
        <button
          className="pm-rt-back"
          onClick={() =>
            navigate(
              currentProject
                ? `/project_management/program-management/delivery-execution/execution-control-center?project=${encodeURIComponent(currentProject)}`
                : '/project_management/program-management/delivery-execution/execution-control-center',
            )
          }
        >
          <i className="material-icons" style={{ fontSize: 16 }}>
            arrow_back
          </i>{' '}
          Execution Control Center
        </button>
        <div className="pm-rt-titleblock">
          <i className="material-icons" style={{ fontSize: '2.1rem', color: '#00897b' }}>
            insights
          </i>
          <div>
            <h1 className="page-header__title">Resource Tracker</h1>
            <p>Actual effort vs. plan, per opportunity and phase — with a dedicated effort log tab.</p>
          </div>
        </div>
        <div className="pm-rt-toolbar">
          <div className="pm-rt-fg">
            <SelectField
              label="Project"
              width="lg"
              placeholder={PROMPT.project}
              value={currentProject}
              onChange={(v) => void onSelect(v)}
              options={projects.map((p) => ({
                value: p.delivery_project_id,
                id: p.delivery_project_id,
                description: p.description,
              }))}
            />
          </div>
          {prog ? (
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
              {activeTab === 'breakdown' ? (
                <>
                  <button className="pm-rt-linkbtn" onClick={() => expandAll(true)}>
                    Expand all
                  </button>
                  <button className="pm-rt-linkbtn" onClick={() => expandAll(false)}>
                    Collapse all
                  </button>
                  <span style={{ color: '#cfd8dc' }}>|</span>
                </>
              ) : null}
              <span className="pm-rt-summary">
                {pdFmt(totals.actual)} / {pdFmt(totals.planned)}
              </span>
              <button className="pm-rt-save" disabled={!dirty || saving} onClick={() => void save()}>
                {saveLabel}
              </button>
            </div>
          ) : null}
        </div>
        <div className="pm-rt-tabs">
          <button
            className={`pm-rt-tab${activeTab === 'breakdown' ? ' active' : ''}`}
            onClick={() => setActiveTab('breakdown')}
          >
            <i className="material-icons" style={{ fontSize: 17 }}>
              account_tree
            </i>{' '}
            Effort breakdown
          </button>
          <button
            className={`pm-rt-tab${activeTab === 'log' ? ' active' : ''}`}
            onClick={() => setActiveTab('log')}
          >
            <i className="material-icons" style={{ fontSize: 17 }}>
              assignment_ind
            </i>{' '}
            Effort log <span className="tcount">{entries.length}</span>
          </button>
        </div>
      </div>

      <div className="pm-rt-body">
        {error ? (
          <div className="pm-rt-empty" style={{ color: '#c62828' }}>
            {error}
          </div>
        ) : null}

        {activeTab === 'breakdown' ? (
          !prog ? (
            <div className="pm-rt-empty">
              Select a program to track actual effort by opportunity and phase.
            </div>
          ) : (
            <>
              <div className="pm-rt-statrow">
                <div className="pm-rt-stat">
                  <i className="material-icons ic" style={{ color: '#607d8b' }}>
                    event_note
                  </i>
                  <div>
                    <div className="lbl">Planned Effort</div>
                    <div className="val">{pdFmt(totals.planned)}</div>
                    <div className="sub">Shared + all opportunities</div>
                  </div>
                </div>
                <div className="pm-rt-stat">
                  <i className="material-icons ic" style={{ color: '#00897b' }}>
                    how_to_reg
                  </i>
                  <div>
                    <div className="lbl">Actual Effort</div>
                    <div className="val" style={{ color: '#00695c' }}>
                      {pdFmt(totals.actual)}
                    </div>
                    <div className="sub">from logged effort</div>
                  </div>
                </div>
                <div className="pm-rt-stat">
                  <i
                    className="material-icons ic"
                    style={{ color: totalVar > 0 ? '#c62828' : '#2e7d32' }}
                  >
                    {totalVar > 0 ? 'trending_up' : 'trending_down'}
                  </i>
                  <div>
                    <div className="lbl">Variance</div>
                    <div className={`val ${varClass(totalVar)}`}>
                      {totalVar > 0.05 ? '+' : ''}
                      {pdFmt(totalVar)}
                    </div>
                    <div className="sub">
                      {totalVar > 0.05 ? 'over plan' : totalVar < -0.05 ? 'under plan' : 'on plan'}
                    </div>
                  </div>
                </div>
              </div>

              <table className="pm-rt-grid">
                <colgroup>
                  <col style={{ width: 44 }} />
                  <col />
                  <col style={{ width: 90 }} />
                  <col style={{ width: 80 }} />
                  <col style={{ width: 120 }} />
                  <col style={{ width: 130 }} />
                  <col style={{ width: 120 }} />
                </colgroup>
                <thead>
                  <tr>
                    <th className="ind" />
                    <th>Scope / Phase</th>
                    <th className="num">Pl. FTE</th>
                    <th className="num">Days</th>
                    <th className="num">Pl. Effort</th>
                    <th className="num">Actual</th>
                    <th className="num">Variance</th>
                  </tr>
                </thead>
                <tbody>
                  {renderScope('shared', '🌐 Shared / Project', null)}
                  {prog.opportunities.map((op) =>
                    renderScope(`opp:${op.opportunity_id}`, `📌 ${op.opportunity_id}`, op),
                  )}
                </tbody>
              </table>
            </>
          )
        ) : (
          <>
            <div className="pm-rt-ledgerbar">
              <span className="lb-title">
                <i className="material-icons" style={{ color: '#00897b' }}>
                  assignment_ind
                </i>{' '}
                Effort log <span className="lb-sum">{pdFmt(logSum)}</span>
              </span>
            </div>
            {!prog ? (
              <div className="pm-rt-plempty">Select a program to see and log effort.</div>
            ) : (
              <>
                <div className="pm-rt-addbar">
                  <div className="fld">
                    <label>Scope</label>
                    <select
                      className="pm-rt-in scope"
                      value={effScope}
                      onChange={(e) => setEffScope(e.target.value)}
                    >
                      <option value="">🌐 Shared / Project</option>
                      {prog.opportunities.map((o) => (
                        <option key={o.opportunity_id} value={o.opportunity_id}>
                          📌 {o.opportunity_id}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="fld">
                    <label>Phase</label>
                    <select
                      className="pm-rt-in phase"
                      value={effPhase}
                      onChange={(e) => setEffPhase(e.target.value)}
                    >
                      {prog.phases.map((p) => (
                        <option key={p.key} value={p.key}>
                          {p.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="fld">
                    <label>Resource</label>
                    <input
                      type="text"
                      className="pm-rt-in resource"
                      placeholder="Person or role"
                      value={effResource}
                      onChange={(e) => setEffResource(e.target.value)}
                    />
                  </div>
                  <div className="fld">
                    <label>Vendor</label>
                    <input
                      type="text"
                      className="pm-rt-in vendor"
                      placeholder="optional"
                      value={effVendor}
                      onChange={(e) => setEffVendor(e.target.value)}
                    />
                  </div>
                  <div className="fld">
                    <label>FTE</label>
                    <input
                      type="number"
                      min={0}
                      step={0.1}
                      className="pm-rt-in num"
                      placeholder="1"
                      value={effFte}
                      onChange={(e) => setEffFte(e.target.value)}
                    />
                  </div>
                  <div className="fld">
                    <label>Days</label>
                    <input
                      type="number"
                      min={0}
                      step={1}
                      className="pm-rt-in num"
                      placeholder="0"
                      value={effDays}
                      onChange={(e) => setEffDays(e.target.value)}
                    />
                  </div>
                  <div className="fld">
                    <label>Start</label>
                    <input
                      type="date"
                      className="pm-rt-in dt"
                      value={effStart}
                      onChange={(e) => setEffStart(e.target.value)}
                    />
                  </div>
                  <div className="fld">
                    <button className="pm-rt-addbtn" onClick={() => void addEntry()}>
                      + Add
                    </button>
                  </div>
                </div>

                <div className="pm-rt-plwrap">
                  {!entries.length ? (
                    <div className="pm-rt-plempty">
                      No effort logged yet. Add an entry above — it rolls up into that phase&apos;s
                      Actual on the breakdown tab.
                    </div>
                  ) : (
                    <table className="pm-rt-pltable">
                      <thead>
                        <tr>
                          <th>Scope</th>
                          <th style={{ width: 140 }}>Phase</th>
                          <th style={{ width: 150 }}>Resource</th>
                          <th style={{ width: 140 }}>Vendor</th>
                          <th className="num" style={{ width: 65 }}>
                            FTE
                          </th>
                          <th className="num" style={{ width: 65 }}>
                            Days
                          </th>
                          <th className="num" style={{ width: 95 }}>
                            Effort
                          </th>
                          <th style={{ width: 115 }}>Start</th>
                          <th style={{ width: 34 }} />
                        </tr>
                      </thead>
                      <tbody>
                        {entries.map((e) => {
                          const oppId = e.opportunity_id || '';
                          return (
                            <tr key={e.id}>
                              <td className={`pm-rt-plscope ${oppId ? 'opp' : 'shared'}`}>
                                {scopeName(oppId)}
                              </td>
                              <td>{phaseLabel(e.phase)}</td>
                              <td>{e.resource || '—'}</td>
                              <td>{e.vendor || '—'}</td>
                              <td className="num">{fteFmt(e.fte)}</td>
                              <td className="num">{Number(e.duration_days) || 0} d</td>
                              <td className="num">
                                {pdFmt((Number(e.fte) || 0) * (Number(e.duration_days) || 0))}
                              </td>
                              <td>{e.start_date || '—'}</td>
                              <td style={{ textAlign: 'center', width: 36 }}>
                                <button
                                  className="pm-rt-pldel"
                                  title="Delete"
                                  onClick={() => void delEntry(e.id)}
                                >
                                  ×
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
